import httpClient from '@/utils/httpClient';

export const getAgentStatus = () => httpClient.get('/api/user-agents/dashboard/status');
export const getAgentUsage = (params) => httpClient.get('/api/user-agents/dashboard/usage', { params });
export const getAgentAudit = (params) => httpClient.get('/api/user-agents/dashboard/audit', { params });
export const getAgentTasks = (params) => httpClient.get('/api/user-agents/dashboard/tasks', { params });
export const getAgentTaskRuns = (params) => httpClient.get('/api/user-agents/dashboard/task-runs', { params });
export const getAgentDigest = (params) => httpClient.get('/api/user-agents/dashboard/digest', { params });
export const getAgentLogs = (params) => httpClient.get('/api/user-agents/dashboard/logs', { params });
// Unified per-run history (every run + trigger) for one agent group — backs the Runs & History tab.
export const getAgentRuns = (params) => httpClient.get('/api/user-agents/dashboard/runs', { params });

// Scheduled automations. Core validates ownership and passes the VM's own status through,
// so a bad cron arrives as 400 with a message rather than a generic failure.
export const createTask = (data) => httpClient.post('/api/user-agents/dashboard/tasks', data);
export const updateTask = (taskId, data) => httpClient.put(`/api/user-agents/dashboard/tasks/${taskId}`, data);
export const deleteTask = (taskId) => httpClient.delete(`/api/user-agents/dashboard/tasks/${taskId}`);

export const pauseTask = (taskId) => httpClient.post(`/api/user-agents/dashboard/tasks/${taskId}/pause`);

// Event automations — "when this happens on my profile, have this agent do this". They live in Core,
// not on the VM, because Core raises the events. A 503 means the table has not been migrated yet.
export const getAutomationEvents = () => httpClient.get('/api/user-agents/dashboard/event-automations/events');
export const getEventAutomations = () => httpClient.get('/api/user-agents/dashboard/event-automations');
export const createEventAutomation = (data) => httpClient.post('/api/user-agents/dashboard/event-automations', data);
export const updateEventAutomation = (id, data) => httpClient.put(`/api/user-agents/dashboard/event-automations/${id}`, data);
export const deleteEventAutomation = (id) => httpClient.delete(`/api/user-agents/dashboard/event-automations/${id}`);
// Standing grants: an automation allowed to publish one kind of content without review. Given from the inbox.
export const getStandingGrants = () => httpClient.get('/api/user-agents/dashboard/event-automations/grants');
export const revokeStandingGrant = (id) => httpClient.delete(`/api/user-agents/dashboard/event-automations/grants/${id}`);
export const resumeTask = (taskId) => httpClient.post(`/api/user-agents/dashboard/tasks/${taskId}/resume`);
export const invokeAgent = (data) => httpClient.post('/api/user-agents/dashboard/invoke', data);

// VM host resource usage (CPU/RAM/disk) — 502 until the VM build exposes /api/agents/vm-stats.
export const getVmStats = () => httpClient.get('/api/user-agents/dashboard/vm-stats');
// VM reachability + uptime from Core's SatelliteHealthService, plus the caller's active-agent count.
export const getVmHealth = () => httpClient.get('/api/user-agents/vm-health');
// Team + per-agent spend in USD. Core converts VM tokens at each agent's model rate — do not
// re-derive $ on the client; the model rates live in AgentCostHelper.
// since = 'today' (default) | 'week'
export const getCostSummary = (since = 'today') => httpClient.get('/api/user-agents/dashboard/cost-summary', { params: { since } });
// Cancel a currently-executing run (distinct from pausing a scheduled task's future runs).
export const cancelRun = (runId, group) => httpClient.post(`/api/user-agents/dashboard/runs/${runId}/cancel`, null, { params: { group } });

// ===== Team endpoints (hire/retire/invoke per-profile agents) =====
// Core serializes every Web API response in camelCase (Global.asax.cs), while these screens read the
// rows as PascalCase. Normalize the /team rows once here — read raw, and every `row.Status` /
// `row.GroupName` / `row.Id` across the Agents screens silently reads undefined.
const toPascalRow = (row) =>
  row && typeof row === 'object'
    ? Object.keys(row).reduce((out, k) => {
        out[k.charAt(0).toUpperCase() + k.slice(1)] = row[k];
        return out;
      }, {})
    : row;

export const getTeam = () => httpClient.get('/api/user-agents/team').then((res) => ({ ...res, data: Array.isArray(res?.data) ? res.data.map(toPascalRow) : res?.data }));
export const hireAgent = (data) => httpClient.post('/api/user-agents/team/hire', data);
export const retireAgent = (userAgentId) => httpClient.delete(`/api/user-agents/team/${userAgentId}`);
export const updateAgentSettings = (userAgentId, data) => httpClient.patch(`/api/user-agents/team/${userAgentId}`, data);
// A failed invoke (502 from the VM, 429 at the daily cap) rejects; its body is camelCase too.
export const invokeTeamAgent = (userAgentId, prompt) =>
  httpClient
    .post(`/api/user-agents/team/${userAgentId}/invoke`, { Prompt: prompt })
    .then((res) => ({ ...res, data: toPascalRow(res?.data) }))
    .catch((err) => {
      if (err?.response?.data) err.response.data = toPascalRow(err.response.data);
      throw err;
    });
export const getTeamMessages = (userAgentId, limit) =>
  httpClient
    .get(`/api/user-agents/team/${userAgentId}/messages`, { params: { limit } })
    .then((res) => ({ ...res, data: Array.isArray(res?.data) ? res.data.map(toPascalRow) : res?.data }));

// Team room: every delegation between this profile's agents, newest first (camelCase rows, read as-is).
// agentId (optional) keeps only delegations that agent gave or received.
export const getTeamConversations = (params) => httpClient.get('/api/user-agents/team/conversations', { params });
// Who may hand work to whom: { agents, links: [{ sourceAgentId, targetAgentId, mode, isDefault }] } (camelCase).
// Mode is allow | approve | block; setting null returns the pair to the default. PUT answers with the new view.
export const getTeamLinks = () => httpClient.get('/api/user-agents/team/links');
export const setTeamLink = (data) => httpClient.put('/api/user-agents/team/links', data);
// Stop a delegation waiting for approval or running (a running one finishes on the VM; its result is dropped).
export const cancelDelegation = (taskId) => httpClient.post(`/api/user-agents/team/tasks/${taskId}/cancel`);
// A note to the specialist on a delegation: Core queues it as a linked follow-up task. { Note } → { taskId }.
export const followUpDelegation = (taskId, note) => httpClient.post(`/api/user-agents/team/tasks/${taskId}/follow-up`, { Note: note });

// Emergency stop / undo. Core flips every agent's Status, which gates invoke and delegation.
export const pauseAllAgents = () => httpClient.post('/api/user-agents/team/pause-all');
export const resumeAllAgents = () => httpClient.post('/api/user-agents/team/resume-all');

// ===== Proactive-message unread badge =====
export const getUnreadCounts = () => httpClient.get('/api/user-agents/team/unread-counts');
export const markAgentRead = (userAgentId) => httpClient.post(`/api/user-agents/team/${userAgentId}/mark-read`);

// ===== Agent memory (editable durable facts; Core is source of truth) =====
export const getAgentMemory = (userAgentId) => httpClient.get(`/api/user-agents/team/${userAgentId}/memory`);
export const putAgentMemory = (userAgentId, data) => httpClient.put(`/api/user-agents/team/${userAgentId}/memory`, data);
export const deleteAgentMemory = (userAgentId, kind) => httpClient.delete(`/api/user-agents/team/${userAgentId}/memory/${kind}`);

// Read-only: facts the agent auto-learned from conversations (supermemory on the VM)
export const getAgentLearnedMemory = (userAgentId) => httpClient.get(`/api/user-agents/team/${userAgentId}/learned-memory`);

// Per-agent risk policy (safe/reversible/irreversible → auto|approve). Reuses the AI-Assistant risk taxonomy.
export const getAgentPolicy = (userAgentId) => httpClient.get(`/api/user-agents/team/${userAgentId}/policy`);
export const putAgentPolicy = (userAgentId, body) => httpClient.put(`/api/user-agents/team/${userAgentId}/policy`, body);

// ===== Telegram bot setup (manager only) =====
// Validate token via Telegram getMe — returns { Ok, BotUsername, BotId, FirstName, Error }. Core answers camelCase;
// read raw, a valid token showed as "Invalid token" because `res.data.Ok` was always undefined.
const pascalData = (res) => ({ ...res, data: toPascalRow(res?.data) });
export const validateTelegramBot = (token) => httpClient.post('/api/user-agents/team/telegram/validate', { Token: token }).then(pascalData);
// Detect chat_id by polling getUpdates for "/start <nonce>" — returns { Ok, ChatId, FromName, Error }. The owner opens
// t.me/<bot>?start=<nonce>; a stranger's plain /start is never bound. Use newStartNonce() once per connect attempt.
export const detectTelegramChatId = (token, nonce) => httpClient.post('/api/user-agents/team/telegram/detect-chat', { Token: token, Nonce: nonce }).then(pascalData);
export const newStartNonce = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};
// Give an existing agent its own bot: { Token, ChatId } → { Ok, BotUsername, Error }. Any role, not only the manager.
export const connectTelegramBot = (userAgentId, token, chatId) =>
  httpClient
    .post(`/api/user-agents/team/${userAgentId}/telegram`, { Token: token, ChatId: chatId })
    .then(pascalData)
    .catch((err) => {
      if (err?.response?.data) err.response.data = toPascalRow(err.response.data);
      throw err;
    });
// Take an agent's bot away (it stops on the agent host and its token is deleted there)
export const disconnectTelegramBot = (userAgentId) => httpClient.delete(`/api/user-agents/team/${userAgentId}/telegram`);

// ===== Approvals (human-in-the-loop) =====
// state = 'pending' (default) | 'all'
export const getApprovals = (state = 'pending') => httpClient.get('/api/user-agents/team/approvals', { params: { state } });
// data = { Decision: 'approved'|'rejected'|'edited', Reason?, EditedPreviewJson? }
export const decideApproval = (id, data) => httpClient.post(`/api/user-agents/team/approvals/${id}/decide`, data);

export const getIssues = (params) => httpClient.get('/api/user-agents/issues', { params });
export const getIssue = (id) => httpClient.get(`/api/user-agents/issues/${id}`);
export const createIssue = (data) => httpClient.post('/api/user-agents/issues', data);
export const updateIssue = (id, data) => httpClient.put(`/api/user-agents/issues/${id}`, data);
export const resolveIssue = (id, data) => httpClient.put(`/api/user-agents/issues/${id}/resolve`, data);
export const deleteIssue = (id) => httpClient.delete(`/api/user-agents/issues/${id}`);

export const getGoals = (params) => httpClient.get('/api/user-agents/goals', { params });
export const createGoal = (data) => httpClient.post('/api/user-agents/goals', data);
export const updateGoal = (id, data) => httpClient.put(`/api/user-agents/goals/${id}`, data);
export const updateGoalProgress = (id, data) => httpClient.put(`/api/user-agents/goals/${id}/progress`, data);
export const deleteGoal = (id) => httpClient.delete(`/api/user-agents/goals/${id}`);

export const getDocuments = (params) => httpClient.get('/api/user-agents/documents', { params });
export const getDocument = (id) => httpClient.get(`/api/user-agents/documents/${id}`);
export const createDocument = (data) => httpClient.post('/api/user-agents/documents', data);
export const updateDocument = (id, data) => httpClient.put(`/api/user-agents/documents/${id}`, data);
export const deleteDocument = (id) => httpClient.delete(`/api/user-agents/documents/${id}`);
